import type { CustomImageData } from '@iheartjane/dm-sdk';
import debounce from 'lodash-es/debounce';
import type { MouseEvent } from 'react';
import { useEffect, useEffectEvent, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

import type {
  CustomImageAdProduct,
  WebCustomImageAdEvent,
} from './webCustomImageAd.types';

const SCROLL_SETTLE_MS = 50;
const RENDER_TIMEOUT_MS = 4000;

interface RenderTiming {
  loadedAt: Map<number, number>;
  reported: boolean;
  shownAt?: number;
}

const clickTarget = (
  brandBlock: HTMLElement | null,
  { clientX, clientY, detail }: MouseEvent
): 'image' | 'logo_tagline' => {
  if (!brandBlock || detail === 0) return 'image';

  const { bottom, left, right, top } = brandBlock.getBoundingClientRect();

  return clientX >= left &&
    clientX <= right &&
    clientY >= top &&
    clientY <= bottom
    ? 'logo_tagline'
    : 'image';
};

const furthestVisiblePosition = (row: HTMLElement, scrollLeft: number) => {
  const rowLeft = row.getBoundingClientRect().left - row.scrollLeft;
  let furthest = 0;

  Array.from(row.children).forEach((card, index) => {
    const { left, width } = card.getBoundingClientRect();
    const center = left - rowLeft + width / 2;

    if (center <= scrollLeft + row.clientWidth) {
      furthest = index + 1;
    }
  });

  return furthest;
};

/**
 * Gathers the `WebCustomImageAdEvent`s the ad itself can observe: creative
 * clicks, product images loading, product clicks, and product-row swipes.
 * Attach the returned refs to the ad root, the brand block, and the products
 * row. Render timing starts once the ad is on screen; images that loaded
 * before then count as 0ms.
 */
export const useCustomImageAdAnalytics = (
  customImageData: CustomImageData | undefined,
  productCount: number,
  onEvent: ((event: WebCustomImageAdEvent) => void) | undefined
) => {
  const brandBlockRef = useRef<HTMLDivElement>(null);
  const productsRowRef = useRef<HTMLDivElement>(null);
  const renderRef = useRef<RenderTiming>({
    loadedAt: new Map(),
    reported: false,
  });
  const reportedSwipeRef = useRef(0);

  const { inView, ref: inViewRef } = useInView({ triggerOnce: true });

  const reportRender = (
    render: RenderTiming,
    shownAt: number,
    loadMs: number
  ) => {
    render.reported = true;
    const times = Array.from(render.loadedAt.values(), (loadedAt) =>
      Math.max(0, loadedAt - shownAt)
    );

    onEvent?.({
      avgLoadMs: times.length
        ? Math.round(times.reduce((sum, time) => sum + time, 0) / times.length)
        : undefined,
      loadMs: Math.round(loadMs),
      loadedCount: times.length,
      productCount,
      type: 'rendered_products',
    });
  };

  const reportIfAllLoaded = (render: RenderTiming, shownAt: number) => {
    if (render.loadedAt.size < productCount) return;

    const lastLoadedAt = Math.max(...render.loadedAt.values());
    reportRender(render, shownAt, Math.max(0, lastLoadedAt - shownAt));
  };

  const onShown = useEffectEvent((render: RenderTiming) => {
    const shownAt = performance.now();
    render.shownAt = shownAt;
    reportIfAllLoaded(render, shownAt);

    return setTimeout(() => {
      if (!render.reported) reportRender(render, shownAt, RENDER_TIMEOUT_MS);
    }, RENDER_TIMEOUT_MS);
  });

  useEffect(() => {
    const render = renderRef.current;
    if (!inView || render.reported) return;

    const timeout = onShown(render);

    return () => clearTimeout(timeout);
  }, [inView]);

  const onSwiped = useEffectEvent((row: HTMLElement) => {
    const furthestPosition = furthestVisiblePosition(row, row.scrollLeft);
    const initialPosition = furthestVisiblePosition(row, 0);

    if (
      !onEvent ||
      furthestPosition <= Math.max(reportedSwipeRef.current, initialPosition)
    ) {
      return;
    }

    reportedSwipeRef.current = furthestPosition;
    onEvent({
      furthestPosition,
      productCount: row.children.length,
      type: 'swiped_products',
    });
  });

  useEffect(() => {
    const row = productsRowRef.current;
    if (!row) return;

    const handleScrollSettled = debounce(() => onSwiped(row), SCROLL_SETTLE_MS);

    row.addEventListener('scroll', handleScrollSettled, { passive: true });

    return () => {
      row.removeEventListener('scroll', handleScrollSettled);
      handleScrollSettled.cancel();
    };
  }, [customImageData]);

  return {
    brandBlockRef,
    inViewRef,
    productsRowRef,
    trackCreativeClick: (event: MouseEvent) =>
      onEvent?.({
        target: clickTarget(brandBlockRef.current, event),
        type: 'clicked_ad',
      }),
    trackProductClick: (product: CustomImageAdProduct, index: number) =>
      onEvent?.({ position: index + 1, product, type: 'clicked_product' }),
    trackProductRender: (index: number) => {
      const render = renderRef.current;
      if (render.reported || render.loadedAt.has(index)) return;

      render.loadedAt.set(index, performance.now());
      if (render.shownAt != null) reportIfAllLoaded(render, render.shownAt);
    },
  };
};
